import { FC, useMemo } from 'react';

import { isNil } from 'lodash';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button, Input } from '@components/index';
import { END_POINTS } from '@constants/api-endpoints';
import { InputTypes } from '@enums/input';
import { AppRoutes } from '@routes/constants';

interface LoginFormProps {
  isSSOLogin?: boolean;
  iconComponent?: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | null;
  buttonLabel?: string;
  onLoginButtonClick?: Function;
}

type LoginFormData = {
  email: string;
  password?: string;
};

const LoginForm: FC<LoginFormProps> = ({
  isSSOLogin = false,
  iconComponent = () => null,
  buttonLabel = 'Login',
  onLoginButtonClick,
}) => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<LoginFormData>({
    mode: 'onChange',
  });

  const email = watch('email');
  const password = watch('password');

  const isButtonDisabled = useMemo(
    () => isSubmitting || (isSSOLogin ? !email : !email || !password),
    [isSubmitting, isSSOLogin, email, password],
  );

  const onForgotPasswordButtonClick = () => {
    navigate(AppRoutes.FORGOT_PASSWORD);
  };

  const onFormSubmit = async (data: { email: string; password?: string }) => {
    try {
      if (isSSOLogin) {
        window.location.replace(
          `${"https://api.brandtechbrain.com/api/"}${END_POINTS.AUTH.SSO_LOGIN}?email=${data?.email}`,
        );
      } else {
        const response = onLoginButtonClick && (await onLoginButtonClick(data));
        if (response?.error) {
          setError('password', {
            type: 'Login error',
            message: response.error?.message,
          });
        }
      }
    } catch (error) {
      console.warn(error);
    }
  };

  return (
    <form
      className="space-y-4 animate-slide-in overflow-hidden"
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <Input
        id="email"
        placeholder={
          isSSOLogin ? 'your.name@unilever.com' : 'your.name@email.com'
        }
        label={isSSOLogin ? 'Unilever email account' : 'Email'}
        {...register('email', {
          required: {
            value: true,
            message: 'Please enter an email address',
          },
        })}
        type={InputTypes.EMAIL}
        customInputClass="focus:border-blue-5 focus:bg-blue-6"
        error={isNil(errors.email?.message) ? '' : errors.email.message}
      />
      {!isSSOLogin && (
        <div className="flex flex-col">
          <Input
            id="password"
            placeholder="Enter your password"
            label="Password"
            type={InputTypes.PASSWORD}
            customInputClass="focus:border-blue-5 focus:bg-blue-6"
            {...register('password', {
              required: {
                value: true,
                message: 'Please enter a password',
              },
            })}
            error={
              isNil(errors.password?.message) ? '' : errors.password.message
            }
            showErrorMessage={false}
          />
          <div className="flex justify-between mt-1">
            {typeof errors?.password?.message === 'string' && (
              <span className="text-red-1 text-xs">
                {errors?.password?.message}
              </span>
            )}
            <Button
              label="Forgot password?"
              onClick={onForgotPasswordButtonClick}
              variant="text"
              classOverride="!ml-auto !text-blue-5"
            />
          </div>
        </div>
      )}
      <Button
        variant="primary-blue"
        type="submit"
        label={buttonLabel}
        IconLeftComponent={iconComponent}
        isLoading={isSubmitting}
        isDisabled={isButtonDisabled}
      />
    </form>
  );
};

export default LoginForm;
